import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Car, 
  Store, 
  PlusCircle, 
  LogOut, 
  Menu, 
  X,
  User,
  CreditCard,
  Clock,
  ListTree,
  ChevronDown,
  ChevronRight,
  Wrench,
  Settings
} from 'lucide-react';
import { useState } from 'react';

const Layout = () => {
  const { user, logout, canEdit } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [masterListsOpen, setMasterListsOpen] = useState(
    location.pathname.startsWith('/master-lists')
  );

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/shops', label: 'Shops List', icon: Store },
    { path: '/subscriptions', label: 'Subscription & Credits', icon: CreditCard },
    { path: '/scheduled-jobs', label: 'Scheduled Jobs', icon: Clock },
    ...(canEdit() ? [
      { path: '/shops/new', label: 'Register Shop', icon: PlusCircle }
    ] : [])
  ];

  const masterListItems = [
    { path: '/master-lists/make-model', label: 'Make-Model', icon: Car },
    { path: '/master-lists/regular-services', label: 'Regular Services', icon: Wrench },
    { path: '/master-lists/maintenance-services', label: 'Maintenance Services', icon: Settings },
  ];

  const NavItem = ({ item }) => (
    <NavLink
      to={item.path}
      onClick={() => setSidebarOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${
          isActive
            ? 'bg-[#2563EB] text-white'
            : 'text-[#94A3B8] hover:bg-[#334155]/50 hover:text-[#E2E8F0]'
        }`
      }
      data-testid={`nav-${item.path.replace(/\//g, '-')}`}
    >
      <item.icon className="w-5 h-5" />
      {item.label}
    </NavLink>
  );

  const SubNavItem = ({ item }) => (
    <NavLink
      to={item.path}
      onClick={() => setSidebarOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 pl-10 pr-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
          isActive
            ? 'bg-[#2563EB]/20 text-[#60A5FA]'
            : 'text-[#94A3B8] hover:bg-[#334155]/50 hover:text-[#E2E8F0]'
        }`
      }
      data-testid={`nav-${item.path.replace(/\//g, '-')}`}
    >
      <item.icon className="w-4 h-4" />
      {item.label}
    </NavLink>
  );

  const isMasterListActive = location.pathname.startsWith('/master-lists');

  return (
    <div className="min-h-screen bg-[#0F172A]" data-testid="authenticated-layout">
      {/* Mobile header */}
      <div className="lg:hidden flex items-center justify-between px-4 py-3 bg-[#1E293B] border-b border-[#334155]">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#2563EB] flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-[#E2E8F0]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            AutoPilot
          </span>
        </div>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 text-[#94A3B8] hover:text-[#E2E8F0]"
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-[#1E293B] border-r border-[#334155] z-50 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-[#334155]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#2563EB] flex items-center justify-center">
                <Car className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-[#E2E8F0]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  AutoPilot
                </h1>
                <p className="text-xs text-[#64748B]">Super Admin</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map(item => (
              <NavItem key={item.path} item={item} />
            ))}
            
            {/* Master Lists Submenu */}
            <div>
              <button
                onClick={() => setMasterListsOpen(!masterListsOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                  isMasterListActive
                    ? 'bg-[#2563EB] text-white'
                    : 'text-[#94A3B8] hover:bg-[#334155]/50 hover:text-[#E2E8F0]'
                }`}
                data-testid="nav-master-lists"
              >
                <div className="flex items-center gap-3">
                  <ListTree className="w-5 h-5" />
                  Master Lists
                </div>
                {masterListsOpen ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              
              {masterListsOpen && (
                <div className="mt-1 space-y-1">
                  {masterListItems.map(item => (
                    <SubNavItem key={item.path} item={item} />
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-[#334155]">
            <div className="flex items-center gap-3 px-4 py-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-[#334155] flex items-center justify-center">
                <User className="w-5 h-5 text-[#94A3B8]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#E2E8F0] truncate" data-testid="user-name">
                  {user?.name}
                </p>
                <p className="text-xs text-[#64748B] capitalize" data-testid="user-role">
                  {user?.role === 'superAdmin' ? 'Super Admin' : user?.role === 'edit' ? 'Edit / Create Role' : 'View Role'}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="w-full border-[#334155] text-[#94A3B8] hover:bg-[#334155] hover:text-[#E2E8F0]"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
